import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import { FavoritesProvider } from './contexts/FavoritesContext';

// Code splitting: Lazy load all page components
const HomePage = React.lazy(() => import('./pages/HomePage'));
const ListingDetail = React.lazy(() => import('./pages/ListingDetail'));
const HiveListingDetail = React.lazy(() => import('./pages/HiveListingDetail'));
const HiveBuildingPage = React.lazy(() => import('./pages/HiveBuildingPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const Renting101Page = React.lazy(() => import('./pages/Renting101Page'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const SitemapPage = React.lazy(() => import('./pages/SitemapPage'));
const BlogArticlePage = React.lazy(() => import('./pages/BlogArticlePage'));
// Legacy in-app application UI remains in src/pages (ApplicationPage,
// HiveApplicationPage, PasswordPromptPage) but is unrouted. These paths
// redirect to the prospect portal until intake is turned back on.
const ProspectPortalRedirect = React.lazy(() => import('./pages/ProspectPortalRedirect'));
const ApplicantPortalPage = React.lazy(() => import('./pages/ApplicantPortalPage'));
const HiveCrmVideoTourWatchPage = React.lazy(
  () => import('./pages/hive-crm/HiveCrmVideoTourWatchPage')
);
const MediaShareWatchPage = React.lazy(() => import('./pages/MediaShareWatchPage'));
const LeasingBookingPage = React.lazy(() => import('./pages/LeasingBookingPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const CookiePolicyPage = React.lazy(() => import('./pages/CookiePolicyPage'));
const CareersPage = React.lazy(() => import('./pages/CareersPage'));
const OpenPositionsPage = React.lazy(() => import('./pages/OpenPositionsPage'));
const JobApplicationPage = React.lazy(() => import('./pages/JobApplicationPage'));

const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-peak-red mx-auto mb-4" />
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <GoogleMapsProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}>
      <FavoritesProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/rental-process" element={<Renting101Page />} />
            <Route path="/rental-process/:slug" element={<BlogArticlePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/careers/positions" element={<OpenPositionsPage />} />
            <Route path="/careers/apply" element={<JobApplicationPage />} />
            <Route path="/the-hive" element={<HiveBuildingPage />} />
            <Route path="/listing/:id" element={<ListingDetail />} />
            <Route path="/listing/hive/:id" element={<HiveListingDetail />} />
            <Route path="/application" element={<ProspectPortalRedirect />} />
            <Route path="/application/hive" element={<ProspectPortalRedirect />} />
            <Route path="/edit/:publicUrl" element={<ProspectPortalRedirect />} />
            <Route path="/applicant-portal/:hash" element={<ApplicantPortalPage />} />
            <Route path="/hive-crm/watch/:token" element={<HiveCrmVideoTourWatchPage />} />
            <Route path="/media-sharing/:token" element={<MediaShareWatchPage />} />
            <Route path="/share/:token" element={<MediaShareWatchPage />} />
            <Route path="/book/:token" element={<LeasingBookingPage />} />
            {/* Tenant portal lives at app.amberpeak.ca (separate app) */}
            <Route path="/tenant" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </FavoritesProvider>
    </GoogleMapsProvider>
  );
}

export default App;
